import React, { useEffect, useState } from 'react';
import { ApplicationBackground } from '../components/common/ApplicationBackground';
import microsoftLogo from '../assets/login/microsoft logo.png';
import presidioLogo from '../assets/login/presidio_logo_white.svg';
import ctpLogo from '../assets/login/ctp_logo.png';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { isDevBypassAuthEnabled } from '../utils/auth-utils';

const Login: React.FC = () => {
    const { signIn, isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();
    const [isSSOLoading, setIsSSOLoading] = useState(false);
    const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false);

    useEffect(() => {
        // If user is already authenticated, redirect to dashboard
        if (isAuthenticated && !isLoading) {
            navigate('/');
        }
    }, [isAuthenticated, isLoading, navigate]);

    const handleSSOLogin = async () => {
        if (isSSOLoading) return; // Prevent multiple clicks
        
        setIsSSOLoading(true);
        try {
            await signIn();
            // The signIn function will redirect to the Cognito hosted UI
            // When the user is redirected back, the code in App.tsx will handle the callback
        } catch (error) {
            console.error('Login failed:', error);
            setIsSSOLoading(false); // Reset loading state on error
        }
    };

    const handleBackgroundLoad = () => {
        setIsBackgroundLoaded(true);
    };

    return (
        <div className="relative h-screen w-screen overflow-hidden">
            {/* Background with blurs */}
            <ApplicationBackground onImageLoad={handleBackgroundLoad} />

            {/* Loading overlay - shown until background image loads */}
            {!isBackgroundLoaded && (
                <div className="fixed inset-0 z-20 bg-[#09090B] flex items-center justify-center">
                    <div className="flex flex-col items-center gap-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
                        <p className="text-white text-sm">Loading...</p>
                    </div>
                </div>
            )}

            {/* Main content container */}
            <div className="relative z-10 flex h-full w-full select-none">
                <div className="flex h-full w-full overflow-hidden items-center justify-center px-4 sm:px-0">

                    {/* Login form with glass effect */}
                    <div className='flex flex-col h-full items-center w-full justify-center relative max-w-7xl mx-auto'>

                        {/* Presidio logo */}
                        <div className="mb-6 sm:mb-10">
                            <img src={presidioLogo} alt="Presidio" className="h-5 sm:h-6 text-white" />
                        </div>
                        <div
                            className="flex flex-col w-full max-w-md mx-4 sm:mx-0 sm:w-[30vw] items-start justify-start shadow-lg rounded-xl bg-[#09090B] px-6 sm:px-10 py-8 sm:py-10 gap-2"
                        >
                            {/* Logo and title */}
                            <div className="w-full text-center">

                            <img src={ctpLogo} alt="CTP" className="h-6 text-white" />
                            </div>

                            {/* Subtitle */}
                            <div>
                                <p className="text-start text-white font-semibold italic text-sm sm:text-base">
                                    Welcome to TechProfile 2.0👋
                                </p>
                                <p className="text-start text-white font-semibold italic text-sm sm:text-base">
                                    Manage and view your customer data<br className="hidden sm:block" />never like before
                                </p>
                            </div>

                            {/* Feature list */}
                            <div className='flex justify-center items-center'>
                                <div className="w-full space-y-3 sm:space-y-4 my-4 sm:my-5 text-white text-xs sm:text-sm">
                                    <div className="flex items-start sm:items-center">
                                        <div className="flex-shrink-0 mt-1 sm:mt-0">
                                            <svg width="20" height="20" className="sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H6C4.93913 15 3.92172 15.4214 3.17157 16.1716C2.42143 16.9217 2 17.9391 2 19V21M16 3.128C16.8578 3.35037 17.6174 3.85126 18.1597 4.55206C18.702 5.25286 18.9962 6.11389 18.9962 7C18.9962 7.88611 18.702 8.74714 18.1597 9.44794C17.6174 10.1487 16.8578 10.6496 16 10.872M22 21V19C21.9993 18.1137 21.7044 17.2528 21.1614 16.5523C20.6184 15.8519 19.8581 15.3516 19 15.13M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <span className="ml-3 sm:ml-4 leading-relaxed">Add customers and their profile</span>
                                    </div>

                                    <div className="flex items-start sm:items-center">
                                        <div className="flex-shrink-0 mt-1 sm:mt-0">
                                            <svg width="20" height="20" className="sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M11.9997 13V2L19.9997 6L11.9997 10M20.5607 10.222C21.0928 11.8621 21.1426 13.6206 20.7041 15.2882C20.2656 16.9559 19.3573 18.4624 18.0872 19.6287C16.8172 20.7949 15.2388 21.5718 13.5399 21.8668C11.841 22.1619 10.0932 21.9626 8.50424 21.2929C6.91532 20.6231 5.55224 19.511 4.57717 18.0889C3.6021 16.6668 3.05609 14.9945 3.00409 13.2709C2.9521 11.5474 3.39631 9.84522 4.28388 8.36689C5.17145 6.88856 6.46502 5.69631 8.01068 4.932M8.00168 9.997C7.50059 10.664 7.17436 11.4458 7.05272 12.2712C6.93109 13.0966 7.01791 13.9392 7.30528 14.7224C7.59265 15.5057 8.07143 16.2045 8.69801 16.7554C9.32458 17.3062 10.079 17.6915 10.8926 17.8762C11.7062 18.0608 12.5531 18.039 13.356 17.8126C14.159 17.5862 14.8926 17.1625 15.4899 16.5801C16.0872 15.9977 16.5293 15.275 16.7759 14.478C17.0225 13.681 17.0657 12.835 16.9017 12.017" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <span className="text-white ml-3 sm:ml-4 leading-relaxed">Assign key goals, priorities and industry trends</span>
                                    </div>

                                    <div className="flex items-start sm:items-center">
                                        <div className="flex-shrink-0 mt-1 sm:mt-0">
                                            <svg width="20" height="20" className="sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M3 3V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H21M8 13H15C15.5523 13 16 13.4477 16 14V16C16 16.5523 15.5523 17 15 17H8C7.44772 17 7 16.5523 7 16V14C7 13.4477 7.44772 13 8 13ZM8 5H18C18.5523 5 19 5.44772 19 6V8C19 8.55228 18.5523 9 18 9H8C7.44772 9 7 8.55228 7 8V6C7 5.44772 7.44772 5 8 5Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <span className="ml-3 sm:ml-4 leading-relaxed">Develop project output charts</span>
                                    </div>

                                    <div className="flex items-start sm:items-center">
                                        <div className="flex-shrink-0 mt-1 sm:mt-0">
                                            <svg width="20" height="20" className="sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 16V21M16 14V21M20 10V21M22 3L13.354 11.646C13.3076 11.6926 13.2524 11.7295 13.1916 11.7547C13.1309 11.7799 13.0658 11.7929 13 11.7929C12.9342 11.7929 12.8691 11.7799 12.8084 11.7547C12.7476 11.7295 12.6924 11.6926 12.646 11.646L9.354 8.354C9.26024 8.26026 9.13308 8.20761 9.0005 8.20761C8.86792 8.20761 8.74076 8.26026 8.647 8.354L2 15M4 18V21M8 14V21" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>

                                        </div>
                                        <span className="ml-3 sm:ml-4 leading-relaxed">Generate maturity output reports and more!</span>
                                    </div>
                                </div>
                            </div>


                            {/* SSO Button */}
                            <button
                                onClick={!isSSOLoading ? handleSSOLogin : undefined}
                                disabled={isSSOLoading}
                                className={`flex items-center justify-center rounded-md w-full px-4 sm:px-6 py-3 shadow-sm transition-all group overflow-hidden ${
                                    isSSOLoading 
                                        ? 'bg-gray-300 cursor-not-allowed' 
                                        : 'bg-white hover:bg-[#e9ebee] hover:shadow-md'
                                }`}
                            >
                                <div className="flex items-center relative h-8">
                                    {/* Logo */}
                                    <img
                                        src={microsoftLogo}
                                        alt="Microsoft"
                                        className={`
                                            absolute left-0 transition-all duration-300
                                            group-hover:left-1/2 group-hover:-translate-x-1/2
                                            h-6 w-6 sm:h-8 sm:w-8
                                        `}
                                        style={{ zIndex: 2 }}
                                    />
                                    {/* Text */}
                                    <span
                                        className={`
                                            text-[#09090B] font-medium ml-8 sm:ml-11 transition-all duration-300 text-sm sm:text-base
                                            group-hover:opacity-0 group-hover:translate-x-4
                                        `}
                                        style={{ zIndex: 1 }}
                                    >
                                        Log In With Presidio SSO
                                    </span>
                                    {/* Spinner */}
                                    {isSSOLoading && (
                                        <div className='flex justify-center my-auto ml-2 items-center h-full'>
                                            <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-t-2 border-b-2 border-white"></div>
                                        </div>
                                    )}
                                </div>
                            </button>

                            {/* Dev login (local only) - shown when REACT_APP_DEV_BYPASS_AUTH=true */}
                            {isDevBypassAuthEnabled() && (
                                <button
                                    type="button"
                                    onClick={() => signIn()}
                                    className="mt-3 flex items-center justify-center rounded-md w-full px-4 sm:px-6 py-3 border border-gray-500 text-gray-300 hover:bg-gray-800/50 transition-all text-sm sm:text-base"
                                >
                                    Dev login (local)
                                </button>
                            )}

                            {/* old button */}
                            {/* <button
                                onClick={!isLoading ? handleSSOLogin : undefined}
                                className="flex items-center justify-center rounded-md w-fit bg-[#0E2A4F] px-6 py-3 shadow-sm transition-all hover:bg-[#062E5E] hover:shadow-md"
                            >
                                <div className='flex items-center'>
                                    <img src={microsoftLogo} alt="Microsoft" className="mr-3 h-8 w-8" />
                                    <span className="text-white font-medium">Log In With Microsoft</span>
                                    {isLoading && (
                                        <div className='flex justify-center my-auto ml-2'>
                                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                        </div>
                                    )}
                                </div>
                            </button> */}

                        </div>

                        <div className='text-white mt-6 sm:mt-8 text-xs sm:text-sm text-center'>Copyright © 2025 • Presidio</div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
